import angular from 'angular';
import template from './tree.html';

const treeTemplate = `
  <div ng-if="!akamTree.filterTerm">
    <ul vs-repeat vs-excess="10" class="akam-tree-viewport">
      <li ng-repeat="item in akamTree.visibleItems track by akamTree.idPropertyFn(item)"
        ng-attr-depth-level="{{ ::akamTree.hashMapFn(item).akamDepthLevel }}"
        ng-class="::{ 'parent-node': akamTree.hashMapFn(item).akamHasChildren,
          'leaf-node': !akamTree.hashMapFn(item).akamHasChildren }"
        ng-click="akamTree.onItemClick(item)">

        <div class="toggle-icon-wrapper"
             ng-class="::akamTree.hashMapFn(item).akamIsCollapsed ? 'children-collapsed' : ''">
          <i ng-if="::akamTree.hashMapFn(item).akamHasChildren"
            class="toggle-icon icon-states"
            ng-class="::akamTree.hashMapFn(item).akamIsCollapsed ?
            'aci-arrow-right' : 'aci-arrow-bottom'"
            ng-click="akamTree.onToggleIconClick($event, item)">
          </i>
        </div>

        <akam-tree-template></akam-tree-template>
      </li>
    </ul>
  </div>

  <div ng-if="akamTree.filterTerm">
    <ul vs-repeat vs-excess="10" class="akam-filter-viewport">
      <li ng-repeat="item in akamTree.filteredItems track by akamTree.idPropertyFn(item)"
          ng-click="akamTree.onItemClick(item)">
        <akam-tree-template></akam-tree-template>
      </li>
    </ul>
  </div>
`;

class TreeController {
  static get $inject() {
    return ['$scope', '$q', '$parse', '$element', '$translate'];
  }

  constructor($scope, $q, $parse, $element, $translate) {
    this.$scope = $scope;
    this.$q = $q;
    this.$parse = $parse;
    this.$element = $element;
    this.$translate = $translate;

    this.resolvedItems = [];
    this.filteredItems = [];
    this.pristineItems = [];
    this.visibleItems = [];
    this.flatItems = [];
    this.hashMap = {};
    this.filterTerm = '';
  }

  $onInit() {
    this.initPropertyFunctions();
    this.translateLabels();

    this.$scope.$watch('$ctrl.items', angular.bind(this, this.loadingFn));
    this.$scope.$watch('$ctrl.isCollapsed', (newValue, oldValue) => {
      if (newValue !== oldValue) {
        this.toggleIsCollapse(newValue || false);
      }
    });
  }

  get isFilterDisabled() {
    return this.loading || !this.flatItems.length;
  }

  hashMapFn(node) {
    return this.hashMap[this.idPropertyFn(node)];
  }

  initPropertyFunctions() {
    if (this.idProperty) {
      this.idPropertyFn = this.$parse(this.idProperty);
    } else {
      throw new Error('idProperty is required');
    }

    if (this.textProperty) {
      this.textPropertyFn = this.$parse(this.textProperty);
    } else {
      throw new Error('textProperty is required');
    }

    if (this.childrenProperty) {
      this.childrenPropertyFn = this.$parse(this.childrenProperty);
    } else {
      throw new Error('childrenProperty is required');
    }
  }

  translateLabels() {
    this.$translate(this.filterPlaceholder || 'components.filter-placeholder')
      .then(msg => this.filterPlaceholderMsg = msg)
      .catch(msg => this.filterPlaceholderMsg = msg);
    this.$translate(this.noFilterResultsMessage || 'components.no-filter-results-message')
      .then(msg => this.noFilterResultsMsg = msg)
      .catch(msg => this.noFilterResultsMsg = msg);
    this.$translate(this.noItemsMessage || 'components.no-items-message')
      .then(msg => this.noItemsMsg = msg)
      .catch(msg => this.noItemsMsg = msg);
  }

  loadingFn() {
    const doneLoadingFn = (items) => {
      this.resolvedItems = items && items.data ? items.data : items;
      this.loading = false;
      this.renderTree();

      return items;
    };

    const failedLoadingFn = (items) => {
      this.failed = true;
      this.resolvedItems = [];
      this.indeterminateProgressLabel = this.failureMessage || 'components.failure-message';

      return items;
    };

    this.loading = true;
    this.failed = false;
    this.resolvedItems = [];
    this.filteredItems = [];
    this.pristineItems = [];
    this.visibleItems = [];
    this.flatItems = [];
    this.hashMap = {};
    this.filterTerm = '';

    this.indeterminateProgressLabel = this.loadingMessage;

    this.$q.when(this.items)
      .then(doneLoadingFn)
      .catch(failedLoadingFn);
  }

  renderTree() {
    this.depthLevel = 0;

    if (this.resolvedItems && this.resolvedItems.length) {
      this.resolvedItems.forEach(item => {
        this.renderNode(item, 0);
      });

      this.updateVisibleItems();
    }

    this.resolvedItems = [];
  }

  renderNode(node, depthLevel) {
    this.pristineItems.push(angular.copy(node));

    this.hashMap[this.idPropertyFn(node)] = {
      akamDepthLevel: depthLevel,
      akamHasChildren: this.nodeHasChildren(node),
      akamIsCollapsed: this.isCollapsed
    };

    // create a flat array of all nodes
    this.flatItems.push(node);

    // recursive call to add children nodes to this node
    if (this.nodeHasChildren(node)) {
      this.childrenPropertyFn(node).forEach(child => {
        this.renderNode(child, depthLevel + 1);
      });
    }
  }

  updateVisibleItems() {
    const length = this.flatItems.length;
    let tempDepthLevel;

    this.visibleItems.length = 0;

    for (let n = 0; n < length; n++) {
      if (this.hashMapFn(this.flatItems[n]).akamIsCollapsed) {
        this.visibleItems.push(this.flatItems[n]);
        tempDepthLevel = this.hashMapFn(this.flatItems[n]).akamDepthLevel;

        do {
          n++; // hide all the children of this node
        } while (n < length &&
          this.hashMapFn(this.flatItems[n]).akamDepthLevel > tempDepthLevel);

        n--;
      } else {
        this.visibleItems.push(this.flatItems[n]);
      }
    }
  }

  onToggleIconClick(event, item) {
    const selectedNode = event.target.parentNode;
    const icon = event.target.classList;
    const flatNode = this.getFlatNode(item);

    // prevent onItemClick from being called
    event.stopPropagation();

    selectedNode.classList.toggle('children-collapsed');
    const collapse = selectedNode.classList.contains('children-collapsed');

    icon.toggle('aci-arrow-right', collapse);
    icon.toggle('aci-arrow-bottom', !collapse);

    this.hashMapFn(flatNode).akamIsCollapsed = collapse;
    this.updateVisibleItems();
  }

  toggleIsCollapse(collapse) {
    this.flatItems.forEach(item => {
      this.hashMapFn(item).akamIsCollapsed = collapse;
    });

    this.updateVisibleItems();
    this.toggleIconState(collapse);
  }

  toggleIconState(collapse) {
    const collapsibleItems =
      [...this.$element[0].querySelectorAll('.akam-tree-viewport li.parent-node')];

    if (collapsibleItems && collapsibleItems.length) {
      collapsibleItems.forEach(item => {
        const icon = item.querySelector('i');

        item.classList.toggle('children-collapsed', collapse);
        icon.classList.toggle('aci-arrow-right', collapse);
        icon.classList.toggle('aci-arrow-bottom', !collapse);
      });
    }
  }

  onItemClick(item) {
    const originalItem = angular.copy(this.getOriginalNode(item));

    if (angular.isFunction(this.onSelect)) {
      this.onSelect({
        item: originalItem
      });
    } else {
      throw new Error('on-select callback is not defined');
    }

    this.clearFilter();
  }

  filterList() {
    const filterTerm = this.filterTerm.toLowerCase();

    if (angular.isFunction(this.onFilter)) {
      this.filteredItems = this.pristineItems.filter(item =>
        this.onFilter({item, term: this.filterTerm })
      );
    } else if (this.textProperty) {
      this.filteredItems = this.pristineItems.filter(item =>
        this.textPropertyFn(item).toLowerCase().includes(filterTerm));
    } else {
      throw new Error('on-filter callback is not defined');
    }
  }

  messages() {
    return {
      noData: this.flatItems && this.flatItems.length === 0 && !this.loading,
      noFiltered: this.filteredItems && this.filteredItems.length === 0
        && !this.loading && this.filterTerm.length
    };
  }

  clearFilter() {
    this.filterTerm = '';
    this.filteredItems = [];
  }

  nodeHasChildren(item) {
    return this.childrenPropertyFn(item) && this.childrenPropertyFn(item).length;
  }

  getOriginalNode(node) {
    const result = this.pristineItems
      .filter(item => this.idPropertyFn(item) === this.idPropertyFn(node));

    return result ? result[0] : null;
  }

  getFlatNode(node) {
    const result = this.flatItems
      .filter(item => this.idPropertyFn(item) === this.idPropertyFn(node));

    return result ? result[0] : null;
  }
}

function akamTreeControl($compile) {
  return {
    restrict: 'E',
    scope: {},
    bindToController: {
      items: '=',
      isCollapsed: '=?',
      idProperty: '@',
      textProperty: '@',
      childrenProperty: '@',
      filterPlaceholder: '@',
      noFilterResultsMessage: '@',
      loadingMessage: '@',
      failureMessage: '@',
      noItemsMessage: '@',
      onFilter: '&?',
      onSelect: '&?'
    },
    controller: TreeController,
    controllerAs: '$ctrl',
    template(element, attributes) {
      const tpl = angular.element(template);
      let itemTemplate = `{{ ::item.${attributes.textProperty} }}`;

      // custom markup
      if (element.html().trim()) {
        itemTemplate = element.html();
      }

      tpl.find('akam-tree-template-placeholder')
        .html(`<akam-tree-template>${itemTemplate}</akam-tree-template>`);

      // remove filter, if necessary.
      if (angular.isDefined(attributes.notFilterable)) {
        angular.element(tpl[0].querySelector('.toolbar')).remove();
      }

      return tpl[0].outerHTML;
    },
    link(scope, element) {
      const el = element.find('akam-tree-template-placeholder'),
        tplEl = angular.element(treeTemplate),
        itemScope = scope.$parent.$new();

      tplEl.find('akam-tree-template').replaceWith(el.html());
      itemScope.akamTree = scope.$ctrl;
      el.replaceWith($compile(tplEl)(itemScope));
    }
  };
}

akamTreeControl.$inject = ['$compile'];
export default akamTreeControl;